import fastdom from 'fastdom'
import { getByCompId as getByCompIdFromDomUtils } from '@wix/thunderbolt-dom-utils'
import type { CyclicTabbing, CyclicTabbingConfig, ElementsIds, EnabledCyclicTabbingComponents } from './types'

const tabbableTags = ['iframe', 'input', 'select', 'textarea', 'button']
const candidateSelectors = [...tabbableTags, 'a[href]', '[tabindex]:not([tabindex="-1"])'].join(',')
const RESTORE_TAB_INDEX_DATA_ATTR = 'data-restore-tabindex'

// static-service path (cyclicTabbingService.ts) gets a serialized config with no DI: dom-utils resolves the
// lookup pair from window.viewerModel.experiments and falls back to getElementById, master's lookup, when closed
const getByCompIdDefault = (compId: string, doc: Document) => getByCompIdFromDomUtils(compId, { document: doc })

export const createCyclicTabbing = ({
	screenReaderFocus = false,
	getByCompId = getByCompIdDefault,
}: CyclicTabbingConfig): CyclicTabbing => {
	let enabledCyclicTabbingComponents: EnabledCyclicTabbingComponents = []

	const toCompIdsArray = (elementsIds: ElementsIds): Array<string> =>
		Array.isArray(elementsIds) ? elementsIds : [elementsIds]
	const toCompIdsKey = (elementsIds: ElementsIds) => toCompIdsArray(elementsIds).join(',')

	const browserWindow = globalThis.window

	const enableCyclicTabbing: CyclicTabbing['enableCyclicTabbing'] = (cyclicTabbingParentCompIds: ElementsIds = []) => {
		if (!browserWindow) {
			return
		}
		if (enabledCyclicTabbingComponents.length > 0) {
			restoreTabIndexes()
		}
		enabledCyclicTabbingComponents.push({
			cyclicTabbingParentCompIds,
		})
		preventElementsTabbing(cyclicTabbingParentCompIds)
	}

	const disableCyclicTabbing: CyclicTabbing['disableCyclicTabbing'] = (cyclicTabbingParentCompIds: Array<string>) => {
		if (!browserWindow) {
			return
		}
		restoreTabIndexes()
		restorePreviousCyclicTabbing(cyclicTabbingParentCompIds)
	}

	const preventElementTabbing = (focusableElement: Element) => {
		const candidateTabIndex =
			focusableElement.getAttribute(RESTORE_TAB_INDEX_DATA_ATTR) || focusableElement.getAttribute('tabindex')

		fastdom.mutate(() => {
			focusableElement.setAttribute('tabindex', '-1')
			focusableElement.setAttribute(RESTORE_TAB_INDEX_DATA_ATTR, `${candidateTabIndex}`)
			if (screenReaderFocus) {
				focusableElement.setAttribute('aria-hidden', 'true')
			}
		})
	}

	function preventElementsTabbing(cyclicTabbingParentCompIds: ElementsIds) {
		// master built an empty selector from [] and never trapped anything; resolving no parents would trap everything
		if (toCompIdsArray(cyclicTabbingParentCompIds).length === 0) {
			return
		}
		function preventElementsTabbingInner() {
			const focusableElements = browserWindow!.document.querySelectorAll(candidateSelectors)
			const excludedParentElements = cyclicTabbingParentCompIds
				? toCompIdsArray(cyclicTabbingParentCompIds)
						.map((compId) => getByCompId(compId, browserWindow!.document))
						.filter((parent): parent is HTMLElement => !!parent)
				: []
			focusableElements.forEach((focusableElement) => {
				if (!excludedParentElements.some((parent) => parent.contains(focusableElement))) {
					preventElementTabbing(focusableElement)
				}
			})
		}

		fastdom.measure(() => {
			preventElementsTabbingInner()
		})
	}

	const restoreTabIndexes = () => {
		fastdom.measure(() => {
			const focusableElements = browserWindow!.document.querySelectorAll(`[${RESTORE_TAB_INDEX_DATA_ATTR}]`)
			focusableElements.forEach((focusableElement) => {
				const restoredTabIndex = focusableElement.getAttribute(RESTORE_TAB_INDEX_DATA_ATTR)

				fastdom.mutate(() => {
					if (restoredTabIndex === 'null') {
						focusableElement.removeAttribute('tabindex')
					} else if (restoredTabIndex) {
						focusableElement.setAttribute('tabindex', restoredTabIndex)
					}
					focusableElement.removeAttribute(RESTORE_TAB_INDEX_DATA_ATTR)
					if (screenReaderFocus) {
						focusableElement.removeAttribute('aria-hidden')
					}
				})
			})
		})
	}

	const restorePreviousCyclicTabbing = (cyclicTabbingParentCompIds: Array<string>) => {
		const parentCompIdsKey = toCompIdsKey(cyclicTabbingParentCompIds)
		enabledCyclicTabbingComponents = enabledCyclicTabbingComponents.filter(
			(component) => toCompIdsKey(component.cyclicTabbingParentCompIds) !== parentCompIdsKey
		)
		const lastModuleInFocus = enabledCyclicTabbingComponents.pop()
		if (lastModuleInFocus) {
			enableCyclicTabbing(lastModuleInFocus.cyclicTabbingParentCompIds)
		}
	}

	return {
		enableCyclicTabbing,
		disableCyclicTabbing,
	}
}
